import { Alert, Form } from 'react-bootstrap';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import type { UserInfoResponse } from '@/app/common/api/types/authApiTypes';
import type { ScheduleMessenger } from '../api/types';
import { WebhookEventHelpTip } from './WebhookEventHelpTip';

export const CHANNEL_BOTS_ACTIVATE_URL =
  'https://accounts.binesh-ec.com/channel-bots?app=mizfa_tools';

const NOTIFICATION_HELP =
  'برای فعال‌سازی اطلاع‌رسانی باید ربات‌های کانال را در پنل بینش وصل کنید؛ بدون آن پیام ارسال نمی‌شود. می‌توانید تلگرام، بله و سایت را با هم فعال کنید. اگر هیچ‌کدام انتخاب نشود، پیام ارسال نمی‌شود. رویدادهای ارسال همان تیک‌های فرم است.';

const MESSENGER_LABELS: Record<ScheduleMessenger, string> = {
  telegram: 'تلگرام',
  bale: 'بله',
  site: 'سایت',
};

/** پیشنهادی فرم‌های زمان‌بندی: تلگرام، بله و اعلان سایت */
export const DEFAULT_SCHEDULE_MESSENGERS: ScheduleMessenger[] = [
  'telegram',
  'bale',
  'site',
];

export function getChannelBotJoinFlags(
  userInfo?: UserInfoResponse | null,
): { joinTelegram: boolean; joinBale: boolean } {
  return {
    joinTelegram: Boolean(userInfo?.data?.join_telegram),
    joinBale: Boolean(userInfo?.data?.join_bale),
  };
}

/** کانال‌های وصل‌نشده را از لیست اطلاع‌رسانی حذف می‌کند. */
export function filterJoinedScheduleMessengers(
  messengers: ScheduleMessenger[],
  userInfo?: UserInfoResponse | null,
): ScheduleMessenger[] {
  const { joinTelegram, joinBale } = getChannelBotJoinFlags(userInfo);
  return messengers.filter((messenger) => {
    if (messenger === 'telegram') return joinTelegram;
    if (messenger === 'bale') return joinBale;
    return true;
  });
}

/** اگر تلگرام یا بله وصل نشده باشد، هشدار فعال‌سازی نشان می‌دهد. */
export function ScheduleChannelBotsAlert({ className }: { className?: string }) {
  const { data: userInfo, isSuccess } = useGetUserInfoQuery();
  const { joinTelegram, joinBale } = getChannelBotJoinFlags(userInfo);
  if (!isSuccess || (joinTelegram && joinBale)) return null;

  const inactive: string[] = [];
  if (!joinTelegram) inactive.push('تلگرام');
  if (!joinBale) inactive.push('بله');

  return (
    <Alert variant="warning" className={`py-2 small ${className ?? ''}`.trim()}>
      برای ارسال پیام از طریق {inactive.join(' و ')} باید کانال را فعال کنید.{' '}
      <a
        href={CHANNEL_BOTS_ACTIVATE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="alert-link"
      >
        فعال‌سازی ربات‌ها
      </a>
    </Alert>
  );
}

export function ScheduleMessengerSelect({
  value,
  onChange,
}: {
  value: ScheduleMessenger[];
  onChange: (value: ScheduleMessenger[]) => void;
}) {
  const { data: userInfo, isSuccess } = useGetUserInfoQuery();
  const { joinTelegram, joinBale } = getChannelBotJoinFlags(userInfo);
  const telegramEnabled = !isSuccess || joinTelegram;
  const baleEnabled = !isSuccess || joinBale;

  function toggle(messenger: ScheduleMessenger, checked: boolean) {
    if (messenger === 'telegram' && !telegramEnabled) return;
    if (messenger === 'bale' && !baleEnabled) return;
    if (checked) {
      onChange([...new Set([...value, messenger])]);
      return;
    }
    onChange(value.filter((item) => item !== messenger));
  }

  return (
    <div className="mb-3">
      <Form.Label className="mb-2 d-flex align-items-center">
        تنظیمات اطلاع‌رسانی
        <WebhookEventHelpTip
          title="تنظیمات اطلاع‌رسانی"
          paragraphs={[NOTIFICATION_HELP]}
        />
      </Form.Label>
      <ScheduleChannelBotsAlert className="mb-2" />
      <Form.Text className="d-block text-secondary mb-2">
        برای فعال‌سازی اطلاع‌رسانی به{' '}
        <a
          href={CHANNEL_BOTS_ACTIVATE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          این صفحه
        </a>{' '}
        مراجعه کنید؛ اگر فعال نکنید امکان اطلاع‌رسانی نیست.
      </Form.Text>
      <div className="d-flex flex-wrap gap-3">
        <Form.Check
          type="checkbox"
          id="schedule-messenger-telegram"
          label={MESSENGER_LABELS.telegram}
          checked={telegramEnabled && value.includes('telegram')}
          disabled={!telegramEnabled}
          onChange={(e) => toggle('telegram', e.target.checked)}
        />
        <Form.Check
          type="checkbox"
          id="schedule-messenger-bale"
          label={MESSENGER_LABELS.bale}
          checked={baleEnabled && value.includes('bale')}
          disabled={!baleEnabled}
          onChange={(e) => toggle('bale', e.target.checked)}
        />
        <Form.Check
          type="checkbox"
          id="schedule-messenger-site"
          label={MESSENGER_LABELS.site}
          checked={value.includes('site')}
          onChange={(e) => toggle('site', e.target.checked)}
        />
      </div>
    </div>
  );
}

export function formatScheduleMessengersLabel(
  messengers: ScheduleMessenger[],
): string {
  if (messengers.length === 0) return '';
  return messengers.map((item) => MESSENGER_LABELS[item] ?? item).join('، ');
}
